import React, {
  createContext,
  useContext,
  useEffect,
  useReducer,
  useCallback,
  useRef,
} from 'react';
import { apiClient } from '../network/ApiClient';
import { showSocialToast } from '../components/social/SocialToastManager';
import { friendChannel } from '../network/FriendChannel';
import { useAuth } from './AuthContext';
import { t as translate } from '../i18n';
import { achievementUnlockedToastText } from '../i18n/achievements';
import type {
  FriendListItem,
  FriendRequestItem,
  GameInvitation,
  Friendship,
  Conversation,
  DirectMessage,
  SocialUser,
} from '../types/social';

// ─── State ───────────────────────────────────────────────────────────────────

/** DM-dock foundation: per-conversation cache bookkeeping. `loaded` means the
 *  newest page has been hydrated at least once this session; `hasMore` mirrors
 *  the server's additive `has_more` (older history exists before the oldest
 *  cached message). Purely data-layer — no presentation state lives here. */
export interface ThreadMeta {
  loading: boolean;
  loaded:  boolean;
  hasMore: boolean;
  error:   string | null;
}

const EMPTY_THREAD_META: ThreadMeta = { loading: false, loaded: false, hasMore: false, error: null };

interface SocialState {
  friends:              FriendListItem[];
  pendingRequests:      FriendRequestItem[];
  incomingInvitations:  GameInvitation[];
  outgoingInvitations:  GameInvitation[];
  conversations:        Conversation[];
  threads:              Record<number, DirectMessage[]>;
  threadMeta:           Record<number, ThreadMeta>;
  /** Highest message id ever received over the cable per counterpart —
   *  redelivery of an already-processed id (reconnect replay) is dropped
   *  wholesale so previews/unread can never double-count, even for threads
   *  that have no hydrated cache to dedup against. */
  lastReceivedMessageIds: Record<number, number>;
  activeThreadFriendId: number | null;
  friendsLoading:       boolean;
  requestsLoading:      boolean;
  invitationsLoading:   boolean;
  conversationsLoading: boolean;
  error:                string | null;
  channelConnected:     boolean;
  lastUnlockedAchievement: { key: string; name: string; description: string; category: string; seq: number } | null;
}

const initialState: SocialState = {
  friends:              [],
  pendingRequests:      [],
  incomingInvitations:  [],
  outgoingInvitations:  [],
  conversations:        [],
  threads:              {},
  threadMeta:           {},
  lastReceivedMessageIds: {},
  activeThreadFriendId: null,
  friendsLoading:       false,
  requestsLoading:      false,
  invitationsLoading:   false,
  conversationsLoading: false,
  error:                null,
  channelConnected:     false,
  lastUnlockedAchievement: null,
};

/** Union-by-id merge keeping chronological order (created_at, then id — the
 *  same total order the backend pages by). Every cache write goes through
 *  this one path, so REST hydration, cable delivery, cable redelivery and
 *  pagination can never insert a duplicate or scramble ordering. */
function mergeMessages(existing: DirectMessage[], incoming: DirectMessage[]): DirectMessage[] {
  if (incoming.length === 0) return existing;
  const byId = new Map<number, DirectMessage>();
  for (const m of existing) byId.set(m.id, m);
  for (const m of incoming) if (!byId.has(m.id)) byId.set(m.id, m);
  return [...byId.values()].sort((a, b) =>
    a.created_at === b.created_at ? a.id - b.id : (a.created_at < b.created_at ? -1 : 1)
  );
}

// ─── Reducer ─────────────────────────────────────────────────────────────────

type Action =
  | { type: 'SET_FRIENDS';             payload: FriendListItem[] }
  | { type: 'SET_PENDING_REQUESTS';    payload: FriendRequestItem[] }
  | { type: 'SET_INVITATIONS';         payload: { incoming: GameInvitation[]; outgoing: GameInvitation[] } }
  | { type: 'SET_CONVERSATIONS';         payload: Conversation[] }
  | { type: 'SET_FRIENDS_LOADING';     payload: boolean }
  | { type: 'SET_REQUESTS_LOADING';    payload: boolean }
  | { type: 'SET_INVITATIONS_LOADING'; payload: boolean }
  | { type: 'SET_CONVERSATIONS_LOADING'; payload: boolean }
  | { type: 'SET_ERROR';               payload: string | null }
  | { type: 'SET_CHANNEL_CONNECTED';   payload: boolean }
  // Incremental state mutations (channel-driven and optimistic)
  | { type: 'APPEND_PENDING_REQUEST';      payload: FriendRequestItem }
  | { type: 'APPEND_FRIEND';              payload: FriendListItem }
  | { type: 'REMOVE_FRIEND';              payload: number }  // friendshipId
  | { type: 'REMOVE_PENDING_REQUEST';     payload: number }  // friendshipId
  | { type: 'REMOVE_INCOMING_INVITATION'; payload: number }  // invitationId
  | { type: 'REMOVE_OUTGOING_INVITATION'; payload: number }  // invitationId
  | { type: 'UPDATE_FRIEND_STATUS'; payload: { user_id: number; status: 'online' | 'in_game' | 'offline'; spectate_game_id?: number | null } }
  | { type: 'SET_THREAD';        payload: { friendId: number; messages: DirectMessage[]; hasMore?: boolean } }
  | { type: 'THREAD_LOADING';    payload: { friendId: number; loading: boolean } }
  | { type: 'THREAD_ERROR';      payload: { friendId: number; error: string | null } }
  | { type: 'PREPEND_OLDER';     payload: { friendId: number; messages: DirectMessage[]; hasMore: boolean } }
  | { type: 'APPEND_MESSAGE';    payload: { friendId: number; message: DirectMessage } }
  | { type: 'SET_ACTIVE_THREAD'; payload: number | null }
  | { type: 'CLEAR_UNREAD';      payload: number }
  | { type: 'REMOVE_CONVERSATION'; payload: number }  // friendId — list entry only, caches untouched
  | { type: 'MESSAGE_RECEIVED';  payload: { message: DirectMessage; from: SocialUser } }
  | { type: 'MESSAGE_SENT';      payload: { friendId: number; message: DirectMessage } }
  | { type: 'ACHIEVEMENT_UNLOCKED'; payload: { key: string; name: string; description: string; category: string } }
  // Reset all state — dispatched on logout to prevent stale data leaking to the next session
  | { type: 'RESET' };

function reducer(state: SocialState, action: Action): SocialState {
  switch (action.type) {
    case 'SET_FRIENDS':
      return { ...state, friends: action.payload, friendsLoading: false };
    case 'SET_PENDING_REQUESTS':
      return { ...state, pendingRequests: action.payload, requestsLoading: false };
    case 'SET_INVITATIONS':
      return {
        ...state,
        incomingInvitations: action.payload.incoming,
        outgoingInvitations: action.payload.outgoing,
        invitationsLoading: false,
      };
    case 'SET_CONVERSATIONS':
      return { ...state, conversations: action.payload, conversationsLoading: false };
    case 'SET_FRIENDS_LOADING':
      return { ...state, friendsLoading: action.payload };
    case 'SET_REQUESTS_LOADING':
      return { ...state, requestsLoading: action.payload };
    case 'SET_INVITATIONS_LOADING':
      return { ...state, invitationsLoading: action.payload };
    case 'SET_CONVERSATIONS_LOADING':
      return { ...state, conversationsLoading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'SET_CHANNEL_CONNECTED':
      return { ...state, channelConnected: action.payload };
    case 'APPEND_PENDING_REQUEST':
      if (state.pendingRequests.some(r => r.friendship_id === action.payload.friendship_id)) return state;
      return { ...state, pendingRequests: [...state.pendingRequests, action.payload] };
    case 'APPEND_FRIEND':
      if (state.friends.some(f => f.friendship_id === action.payload.friendship_id)) return state;
      return { ...state, friends: [...state.friends, action.payload] };
    case 'REMOVE_FRIEND':
      return { ...state, friends: state.friends.filter(f => f.friendship_id !== action.payload) };
    case 'REMOVE_PENDING_REQUEST':
      return { ...state, pendingRequests: state.pendingRequests.filter(r => r.friendship_id !== action.payload) };
    case 'REMOVE_INCOMING_INVITATION':
      return { ...state, incomingInvitations: state.incomingInvitations.filter(i => i.id !== action.payload) };
    case 'REMOVE_OUTGOING_INVITATION':
      return { ...state, outgoingInvitations: state.outgoingInvitations.filter(i => i.id !== action.payload) };
    case 'UPDATE_FRIEND_STATUS':
      return {
        ...state,
        friends: state.friends.map(f =>
          f.friend.id === action.payload.user_id
            // spectate_game_id rides along with this live push (see
            // FriendPresenceNotifier on the backend) so the Spectate button
            // appears/disappears immediately instead of waiting for the next
            // full friends-list refetch (e.g. a page reload).
            ? { ...f, status: action.payload.status, spectate_game_id: action.payload.spectate_game_id ?? null }
            : f
        ),
      };
    case 'SET_THREAD': {
      const { friendId, messages, hasMore } = action.payload;
      // Merge instead of replace: a cable message that landed while the
      // fetch was on the wire (already appended to this cache) survives the
      // hydration — union-by-id makes the order of arrival irrelevant.
      const merged = mergeMessages(state.threads[friendId] ?? [], messages);
      return {
        ...state,
        threads: { ...state.threads, [friendId]: merged },
        threadMeta: {
          ...state.threadMeta,
          [friendId]: {
            ...(state.threadMeta[friendId] ?? EMPTY_THREAD_META),
            loading: false,
            loaded:  true,
            hasMore: hasMore ?? state.threadMeta[friendId]?.hasMore ?? false,
            error:   null,
          },
        },
      };
    }
    case 'THREAD_LOADING': {
      const { friendId, loading } = action.payload;
      return {
        ...state,
        threadMeta: {
          ...state.threadMeta,
          [friendId]: { ...(state.threadMeta[friendId] ?? EMPTY_THREAD_META), loading },
        },
      };
    }
    case 'THREAD_ERROR': {
      const { friendId, error } = action.payload;
      return {
        ...state,
        threadMeta: {
          ...state.threadMeta,
          [friendId]: { ...(state.threadMeta[friendId] ?? EMPTY_THREAD_META), loading: false, error },
        },
      };
    }
    case 'PREPEND_OLDER': {
      const { friendId, messages, hasMore } = action.payload;
      const merged = mergeMessages(state.threads[friendId] ?? [], messages);
      return {
        ...state,
        threads: { ...state.threads, [friendId]: merged },
        threadMeta: {
          ...state.threadMeta,
          [friendId]: {
            ...(state.threadMeta[friendId] ?? EMPTY_THREAD_META),
            loading: false,
            hasMore,
            error:   null,
          },
        },
      };
    }
    case 'APPEND_MESSAGE': {
      const { friendId, message } = action.payload;
      const existing = state.threads[friendId] ?? [];
      return { ...state, threads: { ...state.threads, [friendId]: mergeMessages(existing, [message]) } };
    }
    case 'SET_ACTIVE_THREAD':
      return { ...state, activeThreadFriendId: action.payload };
    case 'CLEAR_UNREAD':
      return {
        ...state,
        conversations: state.conversations.map(c =>
          c.friend.id === action.payload ? { ...c, unread_count: 0 } : c
        ),
      };
    case 'REMOVE_CONVERSATION':
      // "Remove from my list": drops ONLY the list entry (optimistically —
      // the server records the hide). Message caches, thread meta and any
      // open dock window stay untouched; a newer message recreates the row.
      return {
        ...state,
        conversations: state.conversations.filter(c => c.friend.id !== action.payload),
      };
    case 'MESSAGE_RECEIVED': {
      const { message, from } = action.payload;
      // Redelivery guard (reconnect replay): an id at or below the highest
      // already-processed id for this counterpart was fully handled — cache,
      // preview and unread must not run again. Works even for threads with
      // no hydrated cache to dedup against. Ids are server-monotonic.
      const lastSeen = state.lastReceivedMessageIds[from.id] ?? 0;
      const inCache = (state.threads[from.id] ?? []).some(m => m.id === message.id);
      if (message.id <= lastSeen || inCache) return state;

      const isActive = state.activeThreadFriendId === from.id;
      // Multi-conversation model: append (dedup via merge) into ANY hydrated
      // cache — not just the active thread — so every open conversation
      // stays live. Unhydrated threads stay uncached; hydration fetches the
      // authoritative newest page.
      const cached = state.threads[from.id];
      const threads = cached !== undefined || isActive
        ? { ...state.threads, [from.id]: mergeMessages(cached ?? [], [message]) }
        : state.threads;
      // A conversation with no list entry (hidden via "remove from my list",
      // or simply brand-new) REAPPEARS the moment a message arrives — the
      // server applies the same rule, so the next refetch agrees.
      const hasRow = state.conversations.some(c => c.friend.id === from.id);
      const conversations = hasRow
        ? state.conversations.map(c =>
            c.friend.id === from.id
              ? {
                  ...c,
                  last_message:            message.body,
                  last_message_at:         message.created_at,
                  last_from_me:            false,
                  last_message_has_image:  Boolean(message.image_url),
                  unread_count:            isActive ? c.unread_count : c.unread_count + 1,
                }
              : c
          )
        : [
            {
              friend:                 from,
              last_message:           message.body,
              last_message_at:        message.created_at,
              last_from_me:           false,
              last_message_has_image: Boolean(message.image_url),
              unread_count:           isActive ? 0 : 1,
            },
            ...state.conversations,
          ];
      return {
        ...state,
        threads,
        conversations,
        lastReceivedMessageIds: { ...state.lastReceivedMessageIds, [from.id]: message.id },
      };
    }
    case 'MESSAGE_SENT': {
      const { friendId, message } = action.payload;
      return {
        ...state,
        conversations: state.conversations.map(c =>
          c.friend.id === friendId
            ? {
                ...c,
                last_message:           message.body,
                last_message_at:        message.created_at,
                last_from_me:           true,
                last_message_has_image: Boolean(message.image_url),
              }
            : c
        ),
      };
    }
    case 'ACHIEVEMENT_UNLOCKED':
      return {
        ...state,
        lastUnlockedAchievement: { ...action.payload, seq: (state.lastUnlockedAchievement?.seq ?? 0) + 1 },
      };
    case 'RESET':
      return initialState;
    default:
      return state;
  }
}

// ─── Context ─────────────────────────────────────────────────────────────────

interface FriendContextValue extends SocialState {
  /** Badge count: incoming friend requests + incoming game invitations. */
  pendingCount:         number;
  /** Combined initial-load flag across all three data sets. */
  loading:              boolean;
  /** Reload all social data. Call after reconnect or pull-to-refresh. */
  refresh:              () => Promise<void>;
  // ── Friend actions ───────────────────────────────────────────────────────
  /** Send a friend request by target email. Returns the new Friendship row. */
  sendFriendRequest:    (email: string) => Promise<Friendship>;
  /**
   * Send a friend request to a player named via a shared game context
   * (in-game/spectator "Add Friend") instead of email. Returns the new
   * Friendship row.
   */
  sendFriendRequestFromGame: (userId: number, gameId: number) => Promise<Friendship>;
  /** Accept an incoming friend request. Removes it from pendingRequests and refreshes friends. */
  acceptFriendRequest:  (friendshipId: number) => Promise<void>;
  /** Decline an incoming friend request. Removes it from pendingRequests. */
  declineFriendRequest: (friendshipId: number) => Promise<void>;
  /** Remove an accepted friend (or cancel an outgoing sent request). */
  removeFriend:         (friendshipId: number) => Promise<void>;
  // ── Invitation actions ───────────────────────────────────────────────────
  /** Accept an incoming game invitation. Returns the updated invitation. */
  acceptInvitation:     (invitationId: number) => Promise<GameInvitation>;
  /** Decline an incoming game invitation. Returns the updated invitation. */
  declineInvitation:    (invitationId: number) => Promise<GameInvitation>;
  /** Cancel an outgoing game invitation you sent. */
  cancelInvitation:     (invitationId: number) => Promise<void>;
  unreadMessageCount:   number;
  loadConversations:    () => Promise<void>;
  openThread:           (friendId: number) => Promise<DirectMessage[]>;
  closeThread:          () => void;
  sendMessage:          (friendId: number, body: string, image?: File) => Promise<DirectMessage>;
  // ── DM-dock foundation (data layer only — no presentation state) ─────────
  /** Hydrate a conversation's newest page WITHOUT marking anything read. */
  loadThread:           (friendId: number) => Promise<void>;
  /** Fetch the page strictly before the oldest cached message. */
  loadOlderMessages:    (friendId: number) => Promise<void>;
  /** Explicit, idempotent read acknowledgement for one conversation. */
  markConversationRead: (friendId: number) => Promise<void>;
  /**
   * "Remove from my list": hides the conversation from THIS user's list
   * only. No history is deleted, caches and any open dock window stay
   * intact, and a newer message (either direction) makes it reappear.
   */
  hideConversation:     (friendId: number) => Promise<void>;
  /**
   * Presentation gate for DM preview toasts (Docked Chat Redesign): the
   * shell's presentation layer decides WHEN direct-message toasts must stay
   * silent (game/spectate/tutorial routes) and flips this flag — the data
   * layer only obeys it. While suppressed, caches, previews and unread
   * counts keep updating exactly as before; ONLY the DM toast is skipped.
   * No other social toast (friendship, invitation, achievement, connection)
   * is affected by this flag.
   */
  setDmToastsSuppressed: (suppressed: boolean) => void;
}

const FriendContext = createContext<FriendContextValue>({
  ...initialState,
  pendingCount:         0,
  loading:              false,
  refresh:              async () => {},
  sendFriendRequest:    async () => ({ id: 0, status: 'pending', requester_id: 0, addressee_id: 0, created_at: '' }),
  sendFriendRequestFromGame: async () => ({ id: 0, status: 'pending', requester_id: 0, addressee_id: 0, created_at: '' }),
  acceptFriendRequest:  async () => {},
  declineFriendRequest: async () => {},
  removeFriend:         async () => {},
  acceptInvitation:     async () => { throw new Error('FriendContext not mounted'); },
  declineInvitation:    async () => { throw new Error('FriendContext not mounted'); },
  cancelInvitation:     async () => {},
  unreadMessageCount:   0,
  loadConversations:    async () => {},
  openThread:           async () => [],
  closeThread:          () => {},
  sendMessage:          async () => { throw new Error('FriendContext not mounted'); },
  loadThread:           async () => {},
  loadOlderMessages:    async () => {},
  markConversationRead: async () => {},
  hideConversation:     async () => {},
  setDmToastsSuppressed: () => {},
});

// ─── Provider ────────────────────────────────────────────────────────────────

/** Fallback poll interval — presence changes arrive via push events; poll is safety net only. */
const FRIENDS_POLL_MS = 300_000;

/**
 * FriendProvider mounts the Friends & Social state layer.
 *
 * Place inside RequireAuth routes so it is only active for logged-in users.
 * It subscribes to FriendChannel on mount and unsubscribes on unmount.
 *
 * Example (App.tsx, inside the AppShell route):
 *   <FriendProvider>
 *     <Outlet />
 *   </FriendProvider>
 */
export function FriendProvider({ children }: { children: React.ReactNode }) {
  const { isLoggedIn, identityKey } = useAuth();
  const [state, dispatch] = useReducer(reducer, initialState);

  // ── Data fetching ─────────────────────────────────────────────────────────

  // dispatch from useReducer is stable — no need for refs in callbacks.

  const loadFriends = useCallback(async () => {
    dispatch({ type: 'SET_FRIENDS_LOADING', payload: true });
    try {
      const res = await apiClient.listFriends();
      dispatch({ type: 'SET_FRIENDS', payload: res.friends });
    } catch (e) {
      dispatch({ type: 'SET_FRIENDS_LOADING', payload: false });
      dispatch({ type: 'SET_ERROR', payload: (e as Error).message });
    }
  }, []);

  const loadPendingRequests = useCallback(async () => {
    dispatch({ type: 'SET_REQUESTS_LOADING', payload: true });
    try {
      const res = await apiClient.listPendingRequests();
      dispatch({ type: 'SET_PENDING_REQUESTS', payload: res.requests });
    } catch (e) {
      dispatch({ type: 'SET_REQUESTS_LOADING', payload: false });
      dispatch({ type: 'SET_ERROR', payload: (e as Error).message });
    }
  }, []);

  const loadInvitations = useCallback(async () => {
    dispatch({ type: 'SET_INVITATIONS_LOADING', payload: true });
    try {
      const res = await apiClient.listInvitations();
      dispatch({ type: 'SET_INVITATIONS', payload: { incoming: res.incoming, outgoing: res.outgoing } });
    } catch (e) {
      dispatch({ type: 'SET_INVITATIONS_LOADING', payload: false });
      dispatch({ type: 'SET_ERROR', payload: (e as Error).message });
    }
  }, []);

  const loadConversations = useCallback(async () => {
    dispatch({ type: 'SET_CONVERSATIONS_LOADING', payload: true });
    try {
      const res = await apiClient.getConversations();
      dispatch({ type: 'SET_CONVERSATIONS', payload: res.conversations });
    } catch (e) {
      dispatch({ type: 'SET_CONVERSATIONS_LOADING', payload: false });
      dispatch({ type: 'SET_ERROR', payload: (e as Error).message });
    }
  }, []);

  const refresh = useCallback(async () => {
    await Promise.all([loadFriends(), loadPendingRequests(), loadInvitations(), loadConversations()]);
  }, [loadFriends, loadPendingRequests, loadInvitations, loadConversations]);

  const activeThreadFriendIdRef = useRef<number | null>(null);
  useEffect(() => {
    activeThreadFriendIdRef.current = state.activeThreadFriendId;
  }, [state.activeThreadFriendId]);

  // Mirror of the latest state for stable callbacks that need to READ the
  // cache (pagination cursor) without re-creating themselves per render.
  const stateRef = useRef(state);
  useEffect(() => { stateRef.current = state; }, [state]);

  // Synchronous handler-side dedup ledger: every cable message id the
  // handler ACCEPTED, per counterpart. The reducer's own guard protects
  // state, but the toast fires in the handler — so the handler must decide
  // duplicate-or-not itself, synchronously (same-tick redeliveries would
  // otherwise double-toast before React commits the first dispatch).
  // Cleared on identity switch/logout alongside RESET.
  const seenMessageIdsRef = useRef(new Map<number, Set<number>>());

  // DM-dock foundation: monotonically increasing per-identity-session epoch.
  // Every async messaging action captures the epoch at dispatch time and
  // drops its result if the epoch moved before it resolved. Comparing the
  // epoch (not identityKey) closes the A→B→A gap: a request from A's FIRST
  // session that resolves during A's SECOND session sees a different epoch
  // even though the identityKey matches again.
  const sessionEpochRef = useRef(0);

  // Docked Chat Redesign: DM preview toasts stay silent while the
  // presentation layer says so (game/spectate/tutorial). Ref-backed so the
  // channel handler reads it synchronously; state/caches are never gated.
  const dmToastsSuppressedRef = useRef(false);
  const setDmToastsSuppressed = useCallback((suppressed: boolean) => {
    dmToastsSuppressedRef.current = suppressed;
  }, []);

  // ── FriendChannel subscription ────────────────────────────────────────────

  useEffect(() => {
    if (!isLoggedIn) return;
    sessionEpochRef.current += 1;

    void refresh();
    const pollTimer = setInterval(() => {
      void loadFriends(); void loadInvitations(); void loadConversations();
    }, FRIENDS_POLL_MS);

    // Register all 8 event handlers before subscribing.
    friendChannel
      .onFriendRequestReceived((event) => {
        dispatch({
          type: 'APPEND_PENDING_REQUEST',
          payload: { friendship_id: event.friendship_id, from: event.from, sent_at: event.sent_at },
        });
      })
      .onFriendRequestAccepted((event) => {
        // The original requester receives this — their request was accepted.
        dispatch({
          type: 'APPEND_FRIEND',
          payload: {
            friendship_id:    event.friendship_id,
            friend:           event.friend,
            status:           'offline' as const,  // unknown until next poll; corrected on next loadFriends()
            friendship_since: event.friendship_since,
          },
        });
      })
      .onFriendRequestDeclined((event) => {
        void event; // no state mutation — outgoing requests are not tracked
        showSocialToast(translate('friends:toast.requestDeclined'));
      })
      .onFriendRemoved((event) => {
        dispatch({ type: 'REMOVE_FRIEND', payload: event.friendship_id });
      })
      .onGameInvitationReceived(() => {
        // The event payload lacks to_user, which is required for a full GameInvitation.
        // Refresh the invitations list to get complete data.
        void loadInvitations();
      })
      .onGameInvitationAccepted((event) => {
        // Sender receives: their outgoing invitation was accepted.
        dispatch({ type: 'REMOVE_OUTGOING_INVITATION', payload: event.invitation_id });
      })
      .onGameInvitationDeclined((event) => {
        dispatch({ type: 'REMOVE_OUTGOING_INVITATION', payload: event.invitation_id });
        // Invitation notification (Phase 3 scope): resolved at event time in
        // the active language; the friend's name is interpolated verbatim.
        showSocialToast(translate('play:invitation.declined', { name: event.declined_by.display_name }));
      })
      .onGameInvitationCancelled((event) => {
        // Recipient receives: sender cancelled their invitation.
        dispatch({ type: 'REMOVE_INCOMING_INVITATION', payload: event.invitation_id });
      })
      .onPresenceChanged((event) => {
        dispatch({
          type: 'UPDATE_FRIEND_STATUS',
          payload: { user_id: event.user_id, status: event.status, spectate_game_id: event.spectate_game_id },
        });
      })
      .onMessageReceived((event) => {
        // Duplicate delivery (reconnect replay, or a REST hydration that
        // already carried this id) is dropped WHOLESALE here — no dispatch,
        // no toast — so previews/unread/toasts can never double-count.
        const { message, from } = event;
        const seen = seenMessageIdsRef.current.get(from.id) ?? new Set<number>();
        const alreadyCached = (stateRef.current.threads[from.id] ?? []).some(m => m.id === message.id);
        if (seen.has(message.id) || alreadyCached) return;
        seen.add(message.id);
        seenMessageIdsRef.current.set(from.id, seen);

        dispatch({ type: 'MESSAGE_RECEIVED', payload: event });
        if (activeThreadFriendIdRef.current !== event.from.id && !dmToastsSuppressedRef.current) {
          // App-owned surrounding copy localizes at event time; the sender's
          // name and message body are user content, interpolated verbatim.
          const raw = event.message.body.length > 60
            ? `${event.message.body.slice(0, 60)}…`
            : event.message.body;
          const preview = raw || (event.message.image_url ? translate('messages:previewPhoto') : '');
          showSocialToast(translate('messages:toast.incoming', { name: event.from.display_name, text: preview }));
        }
      })
      .onAchievementUnlocked((event) => {
        // Key-based localization at event time; the payload itself is passed
        // through to state unchanged (screens re-localize by key).
        showSocialToast(achievementUnlockedToastText(event.achievement));
        dispatch({ type: 'ACHIEVEMENT_UNLOCKED', payload: event.achievement });
      })
      .onReconnect(() => {
        dispatch({ type: 'SET_CHANNEL_CONNECTED', payload: true });
        void refresh();
        // Reconnect reconciliation (Docked Chat Redesign): messages that
        // arrived while the cable was down never reach a hydrated cache by
        // event, so each hydrated conversation re-syncs through the SAME
        // REST history seam the dock uses — newest page, mark_read=false,
        // union-by-id merge (no duplicates, nothing marked read, no
        // presentation state touched). Bounded: exactly one reconciliation
        // per hydrated conversation per reconnect generation; a thread with
        // a fetch already on the wire is skipped.
        const metaByFriend = stateRef.current.threadMeta;
        for (const key of Object.keys(metaByFriend)) {
          const friendId = Number(key);
          const meta = metaByFriend[friendId];
          if (meta?.loaded && !meta.loading) void loadThread(friendId);
        }
      })
      .onDisconnect(() => {
        dispatch({ type: 'SET_CHANNEL_CONNECTED', payload: false });
      })
      .onRejected(() => {
        // Social realtime degraded (gameplay is unaffected). Mark disconnected so
        // any UI bound to channelConnected reflects the degraded state; the
        // 5-minute REST poll still refreshes friends/invitations as a safety net.
        console.error('[FriendContext] FriendChannel subscription rejected — social realtime degraded');
        dispatch({ type: 'SET_CHANNEL_CONNECTED', payload: false });
      });

    friendChannel.subscribe()
      .then(() => dispatch({ type: 'SET_CHANNEL_CONNECTED', payload: true }))
      .catch(err => console.error('[FriendContext] FriendChannel.subscribe() failed:', err));

    return () => {
      clearInterval(pollTimer);
      friendChannel.unsubscribe();
      sessionEpochRef.current += 1;  // invalidate every in-flight async result immediately
      seenMessageIdsRef.current = new Map();  // no cross-account message-id memory
      dispatch({ type: 'RESET' });  // clear stale data so it never leaks to the next session
    };
  // loadFriends, loadPendingRequests, loadInvitations, refresh are all stable
  // (useCallback with [] deps, dispatch is stable). Keyed on identityKey (not the
  // isLoggedIn boolean) so a DIRECT account switch A→B re-subscribes FriendChannel
  // on B's fresh consumer instead of leaving B with the torn-down one (F2).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identityKey]);

  // ── Actions ───────────────────────────────────────────────────────────────

  const sendFriendRequest = useCallback(async (email: string): Promise<Friendship> => {
    const res = await apiClient.sendFriendRequest(email);
    return res.friendship;
  }, []);

  const sendFriendRequestFromGame = useCallback(async (userId: number, gameId: number): Promise<Friendship> => {
    const res = await apiClient.sendFriendRequestFromGame(userId, gameId);
    return res.friendship;
  }, []);

  const acceptFriendRequest = useCallback(async (friendshipId: number): Promise<void> => {
    await apiClient.acceptFriendRequest(friendshipId);
    dispatch({ type: 'REMOVE_PENDING_REQUEST', payload: friendshipId });
    // Fetch the full friend entry (including online status) from the server.
    void loadFriends();
  }, [loadFriends]);

  const declineFriendRequest = useCallback(async (friendshipId: number): Promise<void> => {
    await apiClient.declineFriendRequest(friendshipId);
    dispatch({ type: 'REMOVE_PENDING_REQUEST', payload: friendshipId });
  }, []);

  const removeFriend = useCallback(async (friendshipId: number): Promise<void> => {
    dispatch({ type: 'REMOVE_FRIEND', payload: friendshipId });  // optimistic
    await apiClient.removeFriend(friendshipId);
  }, []);

  const acceptInvitation = useCallback(async (invitationId: number): Promise<GameInvitation> => {
    const res = await apiClient.acceptInvitation(invitationId);
    dispatch({ type: 'REMOVE_INCOMING_INVITATION', payload: invitationId });
    return res.invitation;
  }, []);

  const declineInvitation = useCallback(async (invitationId: number): Promise<GameInvitation> => {
    const res = await apiClient.declineInvitation(invitationId);
    dispatch({ type: 'REMOVE_INCOMING_INVITATION', payload: invitationId });
    return res.invitation;
  }, []);

  const cancelInvitation = useCallback(async (invitationId: number): Promise<void> => {
    dispatch({ type: 'REMOVE_OUTGOING_INVITATION', payload: invitationId });  // optimistic
    await apiClient.cancelInvitation(invitationId);
  }, []);

  // Legacy screen path — behavior-identical: the request carries NO new
  // params (byte-identical URL), so the server's legacy mark-read side
  // effect still applies, and unread clears exactly as before.
  const openThread = useCallback(async (friendId: number): Promise<DirectMessage[]> => {
    const epoch = sessionEpochRef.current;
    dispatch({ type: 'SET_ACTIVE_THREAD', payload: friendId });
    const res = await apiClient.getMessageThread(friendId);
    if (sessionEpochRef.current !== epoch) return res.messages;
    dispatch({ type: 'SET_THREAD', payload: { friendId, messages: res.messages, hasMore: res.has_more } });
    dispatch({ type: 'CLEAR_UNREAD', payload: friendId });
    return res.messages;
  }, []);

  const closeThread = useCallback((): void => {
    dispatch({ type: 'SET_ACTIVE_THREAD', payload: null });
  }, []);

  // DM-dock foundation: hydrate a conversation's newest page WITHOUT the
  // legacy mark-read side effect and without touching active-thread or
  // unread state. Presentation layers decide separately (via
  // markConversationRead) when a conversation counts as genuinely viewed.
  const loadThread = useCallback(async (friendId: number): Promise<void> => {
    const epoch = sessionEpochRef.current;
    dispatch({ type: 'THREAD_LOADING', payload: { friendId, loading: true } });
    try {
      const res = await apiClient.getMessageThread(friendId, { markRead: false });
      if (sessionEpochRef.current !== epoch) return;
      dispatch({ type: 'SET_THREAD', payload: { friendId, messages: res.messages, hasMore: res.has_more } });
    } catch (e) {
      if (sessionEpochRef.current !== epoch) return;
      dispatch({ type: 'THREAD_ERROR', payload: { friendId, error: (e as Error).message } });
    }
  }, []);

  // DM-dock foundation: fetch the page strictly before the oldest cached
  // message. No-ops when there is nothing cached yet, no older history, or
  // a page is already on the wire for this conversation.
  const loadOlderMessages = useCallback(async (friendId: number): Promise<void> => {
    const epoch = sessionEpochRef.current;
    const cached = stateRef.current.threads[friendId];
    const meta = stateRef.current.threadMeta[friendId];
    if (!cached || cached.length === 0 || !meta?.hasMore || meta.loading) return;
    const beforeId = cached[0].id;
    dispatch({ type: 'THREAD_LOADING', payload: { friendId, loading: true } });
    try {
      const res = await apiClient.getMessageThread(friendId, { beforeId, markRead: false });
      if (sessionEpochRef.current !== epoch) return;
      dispatch({ type: 'PREPEND_OLDER', payload: { friendId, messages: res.messages, hasMore: res.has_more ?? false } });
    } catch (e) {
      if (sessionEpochRef.current !== epoch) return;
      dispatch({ type: 'THREAD_ERROR', payload: { friendId, error: (e as Error).message } });
    }
  }, []);

  // DM-dock foundation: explicit, idempotent read acknowledgement. The data
  // layer only provides the operation — WHEN a conversation counts as read
  // (expanded, focused, visible, not game-suppressed) is the presentation
  // layer's decision, and none of those concepts live here.
  const markConversationRead = useCallback(async (friendId: number): Promise<void> => {
    const epoch = sessionEpochRef.current;
    await apiClient.markConversationRead(friendId);
    if (sessionEpochRef.current !== epoch) return;
    dispatch({ type: 'CLEAR_UNREAD', payload: friendId });
  }, []);

  // "Remove from my list": optimistic — the row disappears immediately; the
  // server records the hide. On failure the authoritative list is refetched
  // so the row comes back instead of silently diverging. Caches, threads and
  // dock windows are deliberately untouched (close ≠ delete; hide ≠ delete).
  const hideConversation = useCallback(async (friendId: number): Promise<void> => {
    const epoch = sessionEpochRef.current;
    dispatch({ type: 'REMOVE_CONVERSATION', payload: friendId });
    try {
      await apiClient.hideConversation(friendId);
    } catch {
      if (sessionEpochRef.current !== epoch) return;
      void loadConversations();
    }
  }, [loadConversations]);

  const sendMessage = useCallback(async (friendId: number, body: string, image?: File): Promise<DirectMessage> => {
    const epoch = sessionEpochRef.current;
    const res = await apiClient.sendMessage(friendId, body, image);
    if (sessionEpochRef.current !== epoch) return res.message;
    dispatch({ type: 'APPEND_MESSAGE', payload: { friendId, message: res.message } });
    dispatch({ type: 'MESSAGE_SENT', payload: { friendId, message: res.message } });
    return res.message;
  }, []);

  // ── Derived values ────────────────────────────────────────────────────────

  const pendingCount = state.pendingRequests.length + state.incomingInvitations.length;
  const loading = state.friendsLoading || state.requestsLoading || state.invitationsLoading;
  const unreadMessageCount = state.conversations.reduce((sum, c) => sum + c.unread_count, 0);

  return (
    <FriendContext.Provider value={{
      ...state,
      pendingCount,
      loading,
      refresh,
      sendFriendRequest,
      sendFriendRequestFromGame,
      acceptFriendRequest,
      declineFriendRequest,
      removeFriend,
      acceptInvitation,
      declineInvitation,
      cancelInvitation,
      unreadMessageCount,
      loadConversations,
      openThread,
      closeThread,
      sendMessage,
      loadThread,
      loadOlderMessages,
      markConversationRead,
      hideConversation,
      setDmToastsSuppressed,
    }}>
      {children}
    </FriendContext.Provider>
  );
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Access the Friends & Social state layer.
 *
 * Must be used inside a component that is a descendant of FriendProvider.
 */
export function useFriends(): FriendContextValue {
  return useContext(FriendContext);
}
